import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from '../../../../components/shareable/dropdownMenu';
import { useDashboard } from '../../../../context/dashboardContext';
import CustomModal from './customModal';
import toast from 'react-hot-toast';
// Option lists and cost formulas are shared with the inline editor in the quote table so
// the two views offer the same choices and compute the same totals.
import {
  EMPTY_DROPDOWN_OPTIONS,
  buildDropdownOptions,
  computeLaborTotal,
  computeLodgingTotal,
  computeInstallationTotal,
  getTechOptions as buildTechOptions,
  getDayOptions as buildDayOptions,
} from './quoteDetailFields';

const QuoteDetailsModal = ({ isOpen, onClose, quoteDetails, setQuoteDetails }) => {
  const { getGlobalOptions } = useDashboard();
  const [installationData, setInstallationData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customType, setCustomType] = useState('');
  const [customCallback, setCustomCallback] = useState(() => {});
  const hasFetchedData = useRef(false);

  const [dropdownOptions, setDropdownOptions] = useState(EMPTY_DROPDOWN_OPTIONS);

  const calculateLaborTotal = () => computeLaborTotal(quoteDetails.installation);
  const calculateLodgingTotal = () => computeLodgingTotal(quoteDetails.installation);
  const calculateInstallationTotal = () => computeInstallationTotal(quoteDetails.installation);

  const getTechOptions = () => buildTechOptions(installationData);
  const getDayOptions = () => buildDayOptions(installationData);

  const handleCustomOptionSelect = (type, setStateFn) => {
    setCustomType(type);
    setCustomCallback(() => (value) => setStateFn(value));
    setCustomModalOpen(true);
  };

  const handleInputChange = (field, value) => {
    setQuoteDetails(prev => ({ ...prev, [field]: value }));
  };

  // Handle numeric input changes to prevent negative numbers and non-numeric characters
  const handleNumericChange = (field, value) => {
    // Remove any non-numeric characters except decimal point
    let sanitizedValue = value.replace(/[^0-9.]/g, '');

    const decimalCount = (sanitizedValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const parts = sanitizedValue.split('.');
      sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Prevent negative numbers by removing leading minus signs
    sanitizedValue = sanitizedValue.replace(/^-/, '');
  
    if (sanitizedValue === '' || sanitizedValue === '.') {
      handleInstallationChange(field, '');
      return;
    }

    handleInstallationChange(field, sanitizedValue);
  };

  const handleInstallationChange = (field, value) => {
    setQuoteDetails(prev => ({ 
      ...prev, 
      installation: { ...prev.installation, [field]: value } 
    }));
  };

  const handleSave = async (updatedDetails) => {
    try {
      setQuoteDetails(updatedDetails);
      toast.success('Changes saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('An error occurred while saving changes');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Prevent multiple API calls
      if (hasFetchedData.current) return;
      
      try {
        setIsLoading(true);
        hasFetchedData.current = true;
        
        const res = await getGlobalOptions();
        if (res?.success) {
          const data = res.data;
          if (data.installation && data.installation.length > 0) {
            setInstallationData(data.installation[0]);
            setQuoteDetails(prev => {
              if (prev.installation) {
                return {
                  ...prev,
                  installation: {
                    ...prev.installation,
                    techs: prev.installation.techs || data.installation[0].Number_of_Service_Technicians?.split(',')[0] || '0',
                    days: prev.installation.days || data.installation[0].Number_of_Days?.split(',')[0] || '0',
                    dayRate: prev.installation.dayRate || data.installation[0].Hourly_Rate || '',
                    lodgingMeals: prev.installation.lodgingMeals || data.installation[0].Lodging_Meals || '',
                    deliveryMethod: prev.installation.deliveryMethod || '',
                    installationOption: prev.installation.installationOption || '',
                    installQuoted: prev.installation.installQuoted || '',
                    address: prev.installation.address || '',
                    city: prev.installation.city || '',
                    state: prev.installation.state || '',
                    zipcode: prev.installation.zipcode || ''
                  }
                };
              }
              return {
                ...prev,
                installation: {
                  techs: data.installation[0].Number_of_Service_Technicians?.split(',')[0] || '0',
                  days: data.installation[0].Number_of_Days?.split(',')[0] || '0',
                  dayRate: data.installation[0].Hourly_Rate || '',
                  lodgingMeals: data.installation[0].Lodging_Meals || '',
                  deliveryMethod: '',
                  installationOption: '',
                  installQuoted: '',
                  address: '',
                  city: '',
                  state: '',
                  zipcode: ''
                }
              };
            });
          }
          setDropdownOptions(prev => ({ ...prev, ...buildDropdownOptions(data) }));
          
          // Set default values for color, inspection, and tax if not already set
          setQuoteDetails(prev => ({
            ...prev,
            color: prev.color || 'BLACK',
            inspection: prev.inspection || 'Customer Inspection At Babs Prior to Shipping',
            tax: prev.tax || 'Texas Conveyor Only 8.25%'
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        hasFetchedData.current = false; // Reset on error to allow retry
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) { 
      fetchData(); 
    } else {
      // Reset the fetch flag when modal is closed so it can fetch again when reopened
      hasFetchedData.current = false;
    }
  }, [isOpen, getGlobalOptions, setQuoteDetails]);

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      <div className="p-6">
        <div className="flex justify-center items-center mb-6 relative">
          <h2 className="text-2xl font-bold text-gray-800 text-center">Terms and Installation Costs</h2>
          <button onClick={onClose} className="absolute right-0 text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Dropdown items={dropdownOptions.colors} label="Conveyor Color" selected={quoteDetails.color} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Conveyor Color', (val) => handleInputChange('color', val)); } else { handleInputChange('color', value); } }} width="w-full" />
                <Dropdown items={dropdownOptions.conveyor_delivery} label="Conveyor Delivery" selected={quoteDetails.conveyorDelivery} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Conveyor Delivery', (val) => handleInputChange('conveyorDelivery', val)); } else { handleInputChange('conveyorDelivery', value); } }} width="w-full" />
                <Dropdown items={dropdownOptions.delivery_terms} label="Delivery Terms" selected={quoteDetails.deliveryTerms} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Delivery Terms', (val) => handleInputChange('deliveryTerms', val)); } else { handleInputChange('deliveryTerms', value); } }} width="w-full" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Dropdown items={dropdownOptions.saw_delivery} label="Saw Delivery" selected={quoteDetails.sawDelivery} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Saw Delivery', (val) => handleInputChange('sawDelivery', val)); } else { handleInputChange('sawDelivery', value); } }} width="w-full" />
                <Dropdown items={dropdownOptions.inspection} label="Inspection & Acceptance" selected={quoteDetails.inspection} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Inspection', (val) => handleInputChange('inspection', val)); } else { handleInputChange('inspection', value); } }} width="w-full" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Dropdown items={dropdownOptions.warranty} label="Warranty" selected={quoteDetails.warranty} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Warranty', (val) => handleInputChange('warranty', val)); } else { handleInputChange('warranty', value); } }} width="w-full" />
                <Dropdown items={dropdownOptions.tax} label="Sales Tax" selected={quoteDetails.tax} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Sales Tax', (val) => handleInputChange('tax', val)); } else { handleInputChange('tax', value); } }} width="w-full" />
              </div>
              <div className="grid grid-cols-1 gap-6">
                <Dropdown items={dropdownOptions.payment} label="Payment Terms" selected={quoteDetails.payment} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Payment Terms', (val) => handleInputChange('payment', val)); } else { handleInputChange('payment', value); } }} width="w-full" />
              </div>
            </div>
            <div className="border-t border-dashed border-gray-300 w-full" />
            <div className="flex flex-col items-center justify-center pt-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Installation Location</h3>
              <div className="w-full">
                <Dropdown items={dropdownOptions.location} label="" selected={quoteDetails.location} onSelect={(value) => handleInputChange('location', value)} width="w-full" />
              </div>
            </div>
            {quoteDetails.location && (
              <div className="mt-4 p-6 border border-gray-200 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Installation Details</h3>
                
                {/* Address fields at the top */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input 
                    type="text" 
                    value={quoteDetails.installation.address || ''} 
                    onChange={(e) => handleInstallationChange('address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input 
                      type="text" 
                      value={quoteDetails.installation.city || ''} 
                      onChange={(e) => handleInstallationChange('city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input 
                      type="text" 
                      value={quoteDetails.installation.state || ''} 
                      onChange={(e) => handleInstallationChange('state', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zipcode</label>
                    <input 
                      type="text" 
                      value={quoteDetails.installation.zipcode || ''} 
                      onChange={(e) => handleInstallationChange('zipcode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Dropdown items={dropdownOptions.delivery_methods} label="Delivery Method" selected={quoteDetails.installation.deliveryMethod} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Delivery Method', (val) => handleInstallationChange('deliveryMethod', val)); } else { handleInstallationChange('deliveryMethod', value); } }} width="w-full" />
                  <Dropdown items={dropdownOptions.installation_options} label="Installation Option" selected={quoteDetails.installation.installationOption} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Installation Option', (val) => handleInstallationChange('installationOption', val)); } else { handleInstallationChange('installationOption', value); } }} width="w-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Dropdown items={dropdownOptions.install_quoted_options} label="Install Quoted" selected={quoteDetails.installation.installQuoted} onSelect={(value) => { if (value.startsWith('Enter Custom')) { handleCustomOptionSelect('Install Quoted', (val) => handleInstallationChange('installQuoted', val)); } else { handleInstallationChange('installQuoted', value); } }} width="w-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1"># Service Techs</label>
                    <Dropdown items={getTechOptions()} label='' selected={quoteDetails.installation.techs} onSelect={(value) => handleInstallationChange('techs', value)} width="w-full" />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Rate $/Hour (applies on selection of techs and days)</label>
                    <input type="text" value={quoteDetails.installation.dayRate || ''} onChange={(e) => handleNumericChange('dayRate', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Days</label>
                    <Dropdown items={getDayOptions()} label='' selected={quoteDetails.installation.days} onSelect={(value) => handleInstallationChange('days', value)} width="w-full" />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Labor Total (Techs × Service Rate × Days × 8 hrs)</label>
                    <input type="text" value={`$${calculateLaborTotal()}`} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lodging & Meals ($/tech/day)</label>
                    <input type="text" value={quoteDetails.installation.lodgingMeals || ''} onChange={(e) => handleNumericChange('lodgingMeals', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                    <span className="text-xs text-gray-500 mt-1">Total: ${calculateLodgingTotal().toFixed(2)} (rate × techs × days)</span>
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Car Rental</label>
                    <input type="text" value={quoteDetails.installation.carRental || ''} onChange={(e) => handleNumericChange('carRental', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tool Shipments</label>
                    <input type="text" value={quoteDetails.installation.toolShipments || ''} onChange={(e) => handleNumericChange('toolShipments', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Airfare</label>
                    <input type="text" value={quoteDetails.installation.airfare || ''} onChange={(e) => handleNumericChange('airfare', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mileage</label>
                    <input type="text" value={quoteDetails.installation.mileage || ''} onChange={(e) => handleNumericChange('mileage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="0.00" />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Travel Time Cost</label>
                    <input type="text" value={quoteDetails.installation.travelTimeCost || ''} onChange={(e) => handleNumericChange('travelTimeCost', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">CONVEYOR SHIPPING PRICE</label>
                  <input type="text" value={quoteDetails.installation.conveyorShippingPrice || ''} onChange={(e) => handleNumericChange('conveyorShippingPrice', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="$0.00" />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">INSTALLATION TOTAL</label>
                  <input type="text" value={`$${calculateInstallationTotal()}`} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-semibold" />
                </div>
                <div className="flex justify-end space-x-4">
                  <button onClick={() => { 
                    handleInstallationChange('techs', '0'); 
                    handleInstallationChange('days', '0'); 
                    handleInstallationChange('dayRate', installationData.Hourly_Rate || '');
                    handleInstallationChange('lodgingMeals', installationData.Lodging_Meals || ''); 
                    handleInstallationChange('carRental', ''); 
                    handleInstallationChange('toolShipments', ''); 
                    handleInstallationChange('airfare', ''); 
                    handleInstallationChange('mileage', '');
                    handleInstallationChange('travelTimeCost', '');
                    handleInstallationChange('conveyorShippingPrice', '');
                    handleInstallationChange('address', '');
                    handleInstallationChange('city', '');
                    handleInstallationChange('state', '');
                    handleInstallationChange('zipcode', '');
                  }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Reset</button>
                </div>
              </div>
            )}
            <div className="mt-8 flex justify-end space-x-4">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => { const updatedDetails = { ...quoteDetails, installation: { ...quoteDetails.installation, laborTotal: calculateLaborTotal(), installationTotal: calculateInstallationTotal() } }; handleSave(updatedDetails); }} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Done</button>
            </div>
          </div>
        )}
      </div>
      <CustomModal isOpen={customModalOpen} onCancel={() => setCustomModalOpen(false)} onConfirm={(value) => { customCallback(value); setCustomModalOpen(false); }} customType={customType} />
    </div>
  </div>
);
};

export default QuoteDetailsModal;

